import {
  alpha,
  Box,
  makeStyles,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  CircularProgress,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { useMemo, useState } from "react";
import { THEME_MODE } from "src/config/constants";
import FigureText from "src/view/PoolMonitoring/Overview/AssetConfigOption/components/FigureText";
import { getNetworkTokens } from "src/config/tokens";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";
import { CHAIN_ALIASES } from "src/wallet-connection/chains";
import { NETWORK } from "src/config/constants";
import { getContractEditPool } from "src/hooks/useContractEditPool";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import useNotify from "src/hooks/useNotify";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { usePoolMonitoringContext } from "src/context/monitoring/poolMonitoringContext";

const useStyle = makeStyles((theme) => ({
  text: {
    color: theme.palette.text.disabled,
  },
  activeItem: {
    backgroundColor: theme.palette.type === THEME_MODE.DARK ? "#7994C1" : "#E1E5EA",
    borderBottom: "1px solid transparent",
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0.5rem 1rem",
    cursor: "pointer",
    borderBottom: `1px solid ${theme.palette.type === THEME_MODE.DARK ? "#3F526F" : "#E1E5EA"}`,
  },
  collapseText: {
    color: theme.palette.text.moreDisabled,
    fontSize: "14px",
    fontWeight: 400,
  },
  activeCollapseText: {
    fontWeight: 500,
  },
  boxItem: {
    backgroundColor: alpha(theme.palette.type === THEME_MODE.DARK ? "#7994C1" : "#E1E5EA", 0.26),
  },
  acceptedStatus: {
    fontWeight: "450",
    color: "#37CB00",
  },
  pendingStatus: {
    fontWeight: "450",
    color: theme.palette.text.disabled,
  },
  cancelStatus: {
    fontWeight: "450",
    color: "#EA4848",
  },
  executedStatus: {
    fontWeight: "450",
    color: theme.palette.primary.main,
  },
}));

const ASSET_DATA = {
  asset: { text: "Asset", type: "token" },
  ltv: { text: "Loan To Value", type: "percent" },
  liquidationThreshold: { text: "Liquidation Threshold", type: "percent" },
  liquidationBonus: { text: "Liquidation Bonus", type: "percent" },
  utilizationOptimal: { text: "Utilization Optimimal", type: "percent" },
  slope1: { text: "Slope 1", type: "percent" },
  slope2: { text: "Slope 2", type: "percent" },
  baseInterestRate: { text: "Base Interest Rate", type: "percent" },
  val: { text: "Value", type: "bool" },
};

export default function MobileDetailRequest({ request, setIsDetail }) {
  const cls = useStyle();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.only("xs"));
  const { chain } = useWeb3Connect();
  const { wallet } = useProviderContext();
  const { errorNotify, successNotify } = useNotify();
  const [isLoadingCancel, setLoadingCancel] = useState(false);
  const [isLoadingExecute, setLoadingExecute] = useState(false);
  const { fetchPoolData } = usePoolMonitoringContext();
  const { pool } = useParams();
  const history = useHistory();

  const tokens = useMemo(() => {
    if (chain.chainId === CHAIN_ALIASES.BSC_MAINNET) return getNetworkTokens(NETWORK.BSC_MAINNET);
    else if (chain.chainId === CHAIN_ALIASES.BSC_TESTNET) return getNetworkTokens(NETWORK.BSC_TESTNET);
  }, [chain.chainIdt]);

  const calculateValue = (value, type) => {
    switch (type) {
      case "percent":
        return `${BigInt(value).toString() / 100}%`;
      case "bool":
        return value ? "true" : "false";
      case "token":
        return tokens[value.toLowerCase()].symbol;
      default:
        return value;
    }
  };

  const handleAction = async (e, type) => {
    e.stopPropagation();
    if (type === "execute") {
      setLoadingExecute(true);
      try {
        const EditPoolContract = getContractEditPool(wallet.signer);
        const _promise = await EditPoolContract.executeTransaction(request["transaction_id"]);
        await _promise.wait();
        fetchPoolData();
        successNotify("Transaction Successful");
        history.push(`/pool-monitoring/${pool}?action=pending`);
      } catch (error) {
        errorNotify(error.message);
        setLoadingExecute(false);
        return;
      }
      setLoadingCancel(false);
    } else if (type === "cancel") {
      setLoadingCancel(true);
      try {
        const EditPoolContract = getContractEditPool(wallet.signer);
        const _promise = await EditPoolContract.revokeConfirmation(request["transaction_id"]);
        await _promise.wait();
        fetchPoolData();
        successNotify("Transaction Successful");
        history.push(`/pool-monitoring/${pool}?action=pending`);
      } catch (error) {
        errorNotify(error.message);
        setLoadingCancel(false);
        return;
      }
      setLoadingCancel(false);
    }
  };

  const statusStyling = () => {
    switch (request.status) {
      case "Accepted":
        return cls.acceptedStatus;
      case "Cancel":
        return cls.cancelStatus;
      case "Executed":
        return cls.executedStatus;
      case "Pending":
        return cls.pendingStatus;
      case "Failure":
        return cls.cancelStatus;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-start" alignItems="center">
        <ArrowBackIosIcon onClick={() => setIsDetail(false)} />
        <Typography variant="h5" className={cls.text}>
          {request["name"]
            .replace(/([A-Z])/g, " $1")
            .charAt(0)
            .toUpperCase() + request["name"].replace(/([A-Z])/g, " $1").slice(1)}
        </Typography>
      </Box>
      <Paper
        style={{ marginTop: "1.5rem", borderRadius: 0, padding: "1rem 0.5rem", marginLeft: -15, marginRight: -15 }}
      >
        <Box padding={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 15 }}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              style={{ fontWeight: "normal", paddingRight: "0.5rem" }}
            >
              Status
            </Typography>
            <Typography variant="body2" className={statusStyling()}>
              {request["status"]}
            </Typography>
          </Box>

          {Object.keys(ASSET_DATA).map((data) => {
            if (request["data"][data] || typeof request["data"][data] === "boolean") {
              return (
                <FigureText
                  key={request["data"][data].toString()}
                  sx={{ display: "flex", alignItems: "baseline" }}
                  title={ASSET_DATA[data].text}
                  value={calculateValue(request["data"][data], ASSET_DATA[data].type)}
                  mr={matches ? 0 : 8}
                  justifyContent={matches ? "space-between" : "flex-start"}
                  width={matches ? "100%" : "inherit"}
                  style={{ marginBottom: 15 }}
                />
              );
            }
          })}
        </Box>
        <Box display="flex" justifyContent="center">
          <Button
            style={{ marginRight: 10 }}
            color="primary"
            variant="outlined"
            disabled={request.status !== "Accepted" && request.status !== "Pending" && isLoadingCancel === false}
            onClick={(e) => handleAction(e, "cancel")}
          >
            {!isLoadingCancel ? "Cancel" : <CircularProgress size={24} />}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={request.status !== "Accepted" && request.status !== "Pending"}
            onClick={(e) => handleAction(e, "execute")}
          >
            {!isLoadingExecute ? "Execute" : <CircularProgress size={24} />}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
